import type {
	ComparisonTable,
	FaqItem,
	MarketingCard,
	RelatedLink,
	SourceLink,
	SwitchGuide,
} from "../pageContent";

interface SeoContentProps {
	headingPrefix: string;
	headingAccent: string;
	description: string;
	useCases: MarketingCard[];
	comparison?: ComparisonTable;
	sources?: SourceLink[];
	switchGuide?: SwitchGuide;
	faqs: FaqItem[];
	relatedLinks: RelatedLink[];
}

export function SeoContent({
	headingPrefix,
	headingAccent,
	description,
	useCases,
	comparison,
	sources,
	switchGuide,
	faqs,
	relatedLinks,
}: SeoContentProps) {
	return (
		<>
			<section className="py-24 md:py-32">
				<div className="mx-auto max-w-6xl px-6">
					<div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
						<div>
							<p className="text-[11px] uppercase tracking-[0.22em] text-brand-300">
								Workflow overview
							</p>
							<h2 className="mt-4 max-w-md text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
								{headingPrefix} <span className="text-white/58">{headingAccent}</span>
							</h2>
							<p className="mt-5 max-w-[35rem] text-base leading-8 text-white/50 md:text-lg">
								{description}
							</p>
						</div>

						<div className="grid gap-4 md:grid-cols-2">
							{useCases.map((item, index) => (
								<article
									key={item.title}
									className={`glass-card p-6 md:p-7 ${index === 1 ? "md:translate-y-8" : ""}`}
								>
									<p className="text-[11px] uppercase tracking-[0.18em] text-white/35">Use case</p>
									<h3 className="mt-3 text-xl font-semibold tracking-tight text-white/90">
										{item.title}
									</h3>
									<p className="mt-4 text-sm leading-7 text-white/48">{item.description}</p>
								</article>
							))}
						</div>
					</div>

					{comparison ? (
						<div className="mt-16">
							<h2 className="text-2xl font-bold tracking-tight text-white md:text-3xl">
								{comparison.title}
							</h2>
							<p className="mt-4 max-w-3xl text-base leading-8 text-white/50">
								{comparison.description}
							</p>
							<div className="mt-6 overflow-x-auto rounded-[1.5rem] border border-white/[0.08] bg-white/[0.03]">
								<table className="w-full min-w-[40rem] border-collapse text-left text-sm">
									<thead>
										<tr className="border-b border-white/[0.08]">
											<th scope="col" className="px-5 py-4 font-semibold text-white/60">
												Feature
											</th>
											<th scope="col" className="px-5 py-4 font-semibold text-brand-300">
												Clapio
											</th>
											<th scope="col" className="px-5 py-4 font-semibold text-white/60">
												{comparison.competitorName}
											</th>
										</tr>
									</thead>
									<tbody>
										{comparison.rows.map((row) => (
											<tr
												key={row.feature}
												className="border-b border-white/[0.05] last:border-b-0"
											>
												<th scope="row" className="px-5 py-4 align-top font-medium text-white/80">
													{row.feature}
												</th>
												<td className="px-5 py-4 align-top leading-6 text-white/70">
													{row.clapio}
												</td>
												<td className="px-5 py-4 align-top leading-6 text-white/45">
													{row.competitor}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							{comparison.footnote ? (
								<p className="mt-4 max-w-3xl text-sm leading-7 text-white/40">
									{comparison.footnote}
								</p>
							) : null}
						</div>
					) : null}

					{sources?.length ? (
						<div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/45">
							<span>Facts checked August 30, 2026:</span>
							{sources.map((source) => (
								<a
									key={source.href}
									href={source.href}
									target="_blank"
									rel="noopener noreferrer"
									className="underline decoration-white/20 underline-offset-2 transition-colors hover:text-white/70"
								>
									{source.label}
								</a>
							))}
						</div>
					) : null}

					{switchGuide ? (
						<div className="mt-16">
							<h2 className="text-2xl font-bold tracking-tight text-white md:text-3xl">
								{switchGuide.title}
							</h2>
							<p className="mt-4 max-w-3xl text-base leading-8 text-white/50">
								{switchGuide.description}
							</p>
							<ol className="mt-6 grid gap-4 md:grid-cols-2">
								{switchGuide.steps.map((step, index) => (
									<li key={step.title} className="glass-card p-6">
										<p className="text-sm font-semibold text-brand-300">Step {index + 1}</p>
										<h3 className="mt-2 text-lg font-semibold tracking-tight text-white/90">
											{step.title}
										</h3>
										<p className="mt-3 text-sm leading-7 text-white/48">{step.description}</p>
									</li>
								))}
							</ol>
						</div>
					) : null}

					<div className="mt-12 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
						<div className="glass-card p-6 md:p-8">
							<p className="text-[11px] uppercase tracking-[0.18em] text-white/35">
								Related comparisons
							</p>
							<h3 className="mt-3 text-2xl font-semibold tracking-tight text-white/90">
								Compare the workflows people usually need
							</h3>
							<p className="mt-4 max-w-[42ch] text-sm leading-7 text-white/48">
								Choose the page that matches your setup, platform, or recording style. Each
								comparison focuses on a different workflow and set of trade-offs.
							</p>
						</div>
						<div className="grid gap-3 md:grid-cols-2">
							{relatedLinks.map((link) => (
								<a
									key={link.href}
									href={link.href}
									className="glass-card rounded-[1.4rem] px-5 py-5 transition-all hover:-translate-y-0.5 hover:border-brand-500/25 active:translate-y-px"
								>
									<span className="block text-sm font-semibold text-white/90">{link.label}</span>
									<span className="mt-2 block text-sm leading-7 text-white/45">
										{link.description}
									</span>
								</a>
							))}
						</div>
					</div>
				</div>
			</section>

			{faqs.length > 0 ? (
				<section id="faq" className="pb-24 md:pb-32">
					<div className="mx-auto max-w-5xl px-6">
						<div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
							<div>
								<p className="text-[11px] uppercase tracking-[0.22em] text-brand-300">
									Answers for buyers
								</p>
								<h2 className="mt-4 max-w-md text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
									Questions people ask before they switch
								</h2>
								<p className="mt-5 text-base leading-8 text-white/50 md:text-lg">
									Clear answers for buyers comparing screen recording software for daily work.
								</p>
							</div>
							<div className="space-y-4">
								{faqs.map((item) => (
									<article key={item.question} className="glass-card p-6">
										<h3 className="text-lg font-semibold tracking-tight text-white/90">
											{item.question}
										</h3>
										<p className="mt-3 text-sm leading-7 text-white/48">{item.answer}</p>
									</article>
								))}
							</div>
						</div>
					</div>
				</section>
			) : null}
		</>
	);
}
